"use client";

import React, { useState } from "react";
import { ChevronUp, ChevronDown, Pencil } from "lucide-react";
import { submitKybApplication } from "../../services/kybService";
import SpinningLoader from "../../components/SpinningLoader";

function ReviewSection({ title, isExpanded, onToggle, onEdit, children }) {
  return (
    <div className="w-full">
      <div
        className={`bg-white border border-[#ebe9ef] flex items-center justify-between pl-4 pr-6 py-6 ${
          isExpanded ? "rounded-t-[16px] border-b-0" : "rounded-[16px]"
        }`}
      >
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-2 flex-1 text-left cursor-pointer"
        >
          <span className="text-[20px] font-semibold leading-[24px] text-[#423E51]">
            {title}
          </span>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-[#423E51]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[#423E51]" />
          )}
        </button>
        <button
          type="button"
          onClick={onEdit}
          className="flex items-center justify-center w-[40px] h-[40px] border border-[#ebe9ef] rounded-full hover:bg-gray-50 transition shrink-0"
        >
          <Pencil className="w-4 h-4 text-[#6d6584]" />
        </button>
      </div>
      {isExpanded && children}
    </div>
  );
}

function DetailRow({ label, value, isLast }) {
  return (
    <>
      <div className="flex items-center justify-between h-[56px] px-4">
        <span className="text-[14px] font-normal leading-[20px] text-[#423E51]">
          {label}
        </span>
        <span className="text-[16px] font-semibold leading-[24px] text-[#423E51] text-right">
          {value || "—"}
        </span>
      </div>
      {!isLast && <div className="border-t border-[#ebe9ef]" />}
    </>
  );
}

export default function ReviewSubmitStep({
  formData,
  onBack,
  goToStep,
  onSubmitSuccess,
}) {
  const [expandedSections, setExpandedSections] = useState({
    businessDetails: false,
    documentation: false,
    directors: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const VALID_SECTIONS = ["businessDetails", "documentation", "directors"];

  const toggleSection = (section) => {
    if (!VALID_SECTIONS.includes(section)) return;
    setExpandedSections((prev) => ({
      businessDetails:
        section === "businessDetails"
          ? !prev.businessDetails
          : prev.businessDetails,
      documentation:
        section === "documentation" ? !prev.documentation : prev.documentation,
      directors: section === "directors" ? !prev.directors : prev.directors,
    }));
  };

  const businessDetailRows = [
    { label: "Business Name", value: formData.businessName },
    { label: "Trading Name", value: formData.tradingName },
    { label: "Business Type", value: formData.businessType },
    { label: "Registration Number", value: formData.registrationNumber },
    { label: "Tax Number", value: formData.taxNumber },
    { label: "Industry / Sector", value: formData.industrySector },
    { label: "Business Address", value: formData.businessAddress },
  ];

  const directors = formData.directors || [];

  const isComplete =
    formData.businessName?.trim() &&
    formData.businessType &&
    formData.industrySector &&
    formData.businessAddress?.trim() &&
    directors.length > 0 &&
    directors.every(
      (d) =>
        d.fullName?.trim() &&
        d.email?.trim() &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim())
    );

  const handleSubmit = async () => {
    if (!isComplete) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await submitKybApplication(formData);
      if (onSubmitSuccess) {
        onSubmitSuccess(result);
      }
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.error || error?.response?.data?.message;

      let message;
      if (serverMsg) {
        message = serverMsg;
      } else if (status === 409) {
        message =
          "You already have an open application. Please wait for it to be reviewed.";
      } else if (status === 429) {
        message = "Too many requests. Please wait a moment and try again.";
      } else if (status === 403) {
        message =
          "You don't have permission to submit this application. Please contact support.";
      } else if (status === 401) {
        message = "Your session has expired. Please log in again.";
      } else if (!error?.response) {
        message =
          "Network error. Please check your internet connection and try again.";
      } else {
        message = `Something went wrong (${status || "unknown"}). Please try again.`;
      }

      setSubmitError(message);
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) {
    return (
      <div className="fixed inset-0 z-50">
        <SpinningLoader />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-10 pt-10 pb-16">
      {/* Heading */}
      <div className="flex flex-col gap-6 items-center w-full text-center">
        <h2 className="text-[32px] font-semibold leading-[40px] text-[#33125f] w-full">
          Review & Submit
        </h2>
        <p className="text-[16px] font-normal leading-[24px] text-[#423E51]">
          Please review all information before submitting your application. You
          can edit any section by clicking the edit icon.
        </p>
      </div>

      {/* Sections */}
      <div className="flex flex-col gap-6 w-full">
        {/* Business Details */}
        <ReviewSection
          title="Business Details"
          isExpanded={expandedSections.businessDetails}
          onToggle={() => toggleSection("businessDetails")}
          onEdit={() => goToStep(1)}
        >
          <div className="border border-[#ebe9ef] rounded-b-[16px] py-1">
            {businessDetailRows.map((row, index) => (
              <DetailRow
                key={row.label}
                label={row.label}
                value={row.value}
                isLast={index === businessDetailRows.length - 1}
              />
            ))}
          </div>
        </ReviewSection>

        {/* Business Documentation - hidden until implemented */}

        {/* Business Directors */}
        <ReviewSection
          title="Business Directors"
          isExpanded={expandedSections.directors}
          onToggle={() => toggleSection("directors")}
          onEdit={() => goToStep(2)}
        >
          <div className="border border-[#ebe9ef] rounded-b-[16px] py-1">
            {directors.length === 0 ? (
              <div className="flex items-center justify-center h-[56px] px-4">
                <span className="text-[14px] text-[#6d6584]">
                  No directors added yet.
                </span>
              </div>
            ) : (
              directors.map((director, index) => (
                <React.Fragment key={index}>
                  <div className="flex items-center justify-between h-[56px] px-4">
                    <span className="text-[14px] font-normal leading-[20px] text-[#423E51]">
                      {director.fullName}
                    </span>
                    <span className="text-[16px] font-semibold leading-[24px] text-[#423E51] text-right">
                      {director.email}
                    </span>
                  </div>
                  {index < directors.length - 1 && (
                    <div className="border-t border-[#ebe9ef]" />
                  )}
                </React.Fragment>
              ))
            )}
          </div>
        </ReviewSection>
      </div>

      {/* Error Message */}
      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-[16px] p-4">
          <p className="text-[14px] text-red-600 text-center">{submitError}</p>
        </div>
      )}

      {/* Buttons */}
      <div className="flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={onBack}
          className="h-[48px] w-[150px] border border-[#ebe9ef] rounded-full text-[14px] font-semibold text-[#6d6584] hover:bg-gray-50 transition"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isComplete}
          className={`h-[48px] px-6 rounded-full text-[14px] font-semibold transition whitespace-nowrap ${
            isComplete
              ? "text-white cursor-pointer hover:opacity-90"
              : "bg-[#f2f1f4] text-[#9d96af] cursor-not-allowed"
          }`}
          style={
            isComplete
              ? {
                  backgroundImage:
                    "linear-gradient(65deg, #57249C 10.66%, #EB3366 113.47%)",
                }
              : undefined
          }
        >
          Submit Application
        </button>
      </div>
    </div>
  );
}
